import React, { useEffect, useState } from 'react';
import { Box, Button, Typography, Card, CardContent } from '@mui/material';
import { Assignment, Refresh } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { TrainingRequests } from 'requests/TrainingRequests';
import Layout from 'Component/Layouts/layout';
import NewTableComponent from 'Component/NewTable';
import rootAxios from 'requests/rootAxios';
import 'react-toastify/dist/ReactToastify.css';

export default function CertificationLogs() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0);

  // Utility function to parse DD/MM/YYYY format
  const parseDate = (dateString) => {
    if (!dateString) return null;
    const parts = dateString.split('/');
    if (parts.length === 3) {
      // Convert DD/MM/YYYY to MM/DD/YYYY for JavaScript Date constructor
      const day = parts[0];
      const month = parts[1];
      const year = parts[2];
      return new Date(`${month}/${day}/${year}`);
    }
    return new Date(dateString);
  };

  const fetchBatches = async () => {
    try {
      setLoading(true);
      const response = await rootAxios.get('/user/getAllBatches');
      if (response?.data?.data?.listOfBatches) {
        const formattedBatches = response.data.data.listOfBatches.map((batch) => ({
          ...batch,
          certificatesSent: batch.certificatesSent || false,
        }));
        setBatches(formattedBatches);
        setCount(formattedBatches.length);
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch batches', {
        theme: 'colored',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBatches();
  }, []);

  const calculateDaysRemaining = (endDate) => {
    console.log('endDate', endDate);
    if (!endDate) return null;

    const today = new Date();
    const end = parseDate(endDate);

    // Check if date is valid
    if (!end || isNaN(end.getTime())) {
      console.error('Invalid date format:', endDate);
      return null;
    }

    const timeDiff = end.getTime() - today.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
    return daysDiff;
  };

  const handleResendCertificate = async (batchId, endDate) => {
    const daysRemaining = calculateDaysRemaining(endDate);

    if (daysRemaining !== null && daysRemaining > 0 && daysRemaining < 15) {
      toast.error(
        `Cannot resend certificate. Only ${daysRemaining} days remaining before certificate can be sent.`,
        {
          theme: 'colored',
        }
      );
      return;
    }

    try {
      setLoading(true);
      await TrainingRequests.generateCertificates(batchId);
      toast.success('Certificates regenerated successfully', {
        theme: 'colored',
      });
      fetchBatches();
    } catch (error) {
      toast.error('Failed to regenerate certificates', {
        theme: 'colored',
      });
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      label: 'Batch ID',
      field: 'batchId',
      sortable: true,
      width: '120px',
    },
    {
      label: 'Trainer ID',
      field: 'empid',
      sortable: true,
      width: '120px',
    },
    {
      label: 'End Date',
      field: 'endDate',
      sortable: true,
      width: '120px',
      render: (row) => {
        if (!row.endDate) return 'N/A';

        const date = parseDate(row.endDate);
        if (!date || isNaN(date.getTime())) {
          return row.endDate; // Return original string if parsing fails
        }

        return date.toLocaleDateString('en-IN', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
      },
    },
    {
      label: 'Days Remaining',
      field: 'daysRemaining',
      sortable: true,
      width: '130px',
      render: (row) => {
        const daysRemaining = calculateDaysRemaining(row.endDate);
        if (daysRemaining === null) return 'N/A';

        if (daysRemaining <= 0) {
          return (
            <span
              style={{
                color: '#2e7d32',
                fontWeight: 'bold',
              }}
            >
              Training Completed
            </span>
          );
        }

        return (
          <span
            style={{
              color: daysRemaining < 15 ? '#d32f2f' : daysRemaining < 30 ? '#ed6c02' : '#2e7d32',
              fontWeight: daysRemaining < 15 ? 'bold' : 'normal',
            }}
          >
            {daysRemaining} days
          </span>
        );
      },
    },
    {
      label: 'Certificates Sent',
      field: 'certificatesSent',
      sortable: true,
      width: '150px',
      render: (row) => (row.certificatesSent ? 'Yes' : 'No'),
    },
    {
      label: 'Actions',
      field: 'actions',
      sortable: false,
      width: '200px',
      render: (row) => {
        const daysRemaining = calculateDaysRemaining(row.endDate);
        const isDisabled = daysRemaining !== null && daysRemaining > 0 && daysRemaining < 15;

        return (
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleResendCertificate(row.batchId, row.endDate)}
            disabled={loading || isDisabled}
            sx={{
              textTransform: 'none',
              opacity: isDisabled ? 0.6 : 1,
              '&:hover': {
                backgroundColor: isDisabled ? '#1976d2' : '#1565c0',
              },
            }}
          >
            {loading ? 'Resending this may take a while...' : 'Resend Certificate'}
          </Button>
        );
      },
    },
  ];

  return (
    <Layout>
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          p: { xs: 2, md: 3 },
        }}
      >
        {/* Professional Header */}
        <Box
          sx={{
            mb: 2,
            background: '#ffffff',
            borderRadius: '8px',
            p: 2,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e2e8f0',
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
          >
            <Box>
              <Typography
                variant="h5"
                fontWeight="600"
                sx={{
                  color: '#1e293b',
                  mb: 0.25,
                }}
              >
                Certification Logs
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontWeight: '400' }}>
                Track and manage training certification status and resend certificates
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchBatches}
              disabled={loading}
              sx={{
                borderRadius: '12px',
                px: 3,
                py: 1.5,
                fontWeight: '600',
                textTransform: 'none',
                borderColor: '#10b981',
                color: '#10b981',
                '&:hover': {
                  borderColor: '#059669',
                  backgroundColor: 'rgba(16, 185, 129, 0.04)',
                },
              }}
            >
              Refresh Data
            </Button>
          </Box>
        </Box>

        {/* Table Section */}
        <Card
          sx={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid #e2e8f0',
            borderRadius: '16px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <NewTableComponent
              tableData={batches}
              columns={columns}
              tableName="Certification Logs"
              count={count}
              loading={loading}
              enableSearch={true}
              enableFilters={false}
              searchPlaceholder="Search certification logs..."
              useWholeRowClicked={false}
              emptyMessage="No certification logs found"
              maxHeight="600px"
            />
          </CardContent>
        </Card>
      </Box>
    </Layout>
  );
}
